// Integration with external services
// TODO: Add environment variables for all API keys

/**
 * Sends form data to Slack
 */
export const sendToSlack = async (formData) => {
  try {
    const slackWebhookUrl = import.meta.env.VITE_SLACK_WEBHOOK_URL;
    
    if (!slackWebhookUrl) {
      console.warn('Slack webhook URL is not set');
      return false;
    }

    const message = {
      text: `🚀 New project request from ${formData.name}`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '📋 New Project Request',
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Name:*\n${formData.name}`,
            },
            {
              type: 'mrkdwn',
              text: `*Email:*\n${formData.email}`,
            },
            {
              type: 'mrkdwn',
              text: `*Company:*\n${formData.company || 'Not specified'}`,
            },
            {
              type: 'mrkdwn',
              text: `*Message:*\n${formData.message}`,
            },
          ],
        },
        {
          type: 'divider',
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Services:*\n${formData.services.join(', ') || 'Not selected'}\n\n*Technologies:*\n${formData.technologies.join(', ') || 'Not selected'}`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Budget:*\n${formData.budget || 'Not specified'}`,
            },
            {
              type: 'mrkdwn',
              text: `*Timeline:*\n${formData.timeline || 'Not specified'}`,
            },
            {
              type: 'mrkdwn',
              text: `*Project Status:*\n${formData.projectStatus.join(', ') || 'Not specified'}`,
            },
            {
              type: 'mrkdwn',
              text: `*Platforms:*\n${formData.platforms.join(', ') || 'Not selected'}`,
            },
          ],
        },
      ],
    };

    const response = await fetch(slackWebhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });

    return response.ok;
  } catch (error) {
    console.error('Error sending to Slack:', error);
    return false;
  }
};

/**
 * Updates an existing lead in D1 with survey data via /api/update-lead
 */
export const updateLeadInD1 = async (leadId, formData) => {
  try {
    const response = await fetch('/api/update-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: leadId,
        services: formData.services,
        technologies: formData.technologies,
        budget: formData.budget,
        timeline: formData.timeline,
        projectStatus: formData.projectStatus,
        platforms: formData.platforms,
        hasDesign: formData.hasDesign,
        hasTechSpec: formData.hasTechSpec,
        developmentStage: formData.developmentStage,
        prototypeLink: formData.prototypeLink,
      }),
    });

    if (!response.ok) {
      throw new Error(`update-lead error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating lead in D1:', error);
    return null;
  }
};

/**
 * Saves data to Cloudflare D1 via the /api/save-lead Pages Function
 */
export const saveToD1 = async (formData, source = 'contact') => {
  try {
    const response = await fetch('/api/save-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: formData.email,
        name: formData.name,
        company: formData.company,
        message: formData.message,
        services: formData.services,
        technologies: formData.technologies,
        budget: formData.budget,
        timeline: formData.timeline,
        projectStatus: formData.projectStatus,
        platforms: formData.platforms,
        hasDesign: formData.hasDesign,
        hasTechSpec: formData.hasTechSpec,
        developmentStage: formData.developmentStage,
        prototypeLink: formData.prototypeLink,
        source,
      }),
    });

    if (!response.ok) {
      throw new Error(`save-lead error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving to D1:', error);
    return null;
  }
};

/**
 * Sends thank you email via Resend (proxied through /api/send-email)
 */
export const sendThankYouEmail = async (formData) => {
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from: 'noreply@bercastle.com',
        to: formData.email,
        subject: 'Thank you for your inquiry! We will contact you soon',
        html: generateThankYouTemplate(formData),
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error sending email:', error);
    return false;
  }
};

/**
 * Generates HTML template for thank you email
 */
const generateThankYouTemplate = (formData) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 8px; }
          .content { padding: 20px; background: #f9fafb; border-radius: 8px; margin-top: 20px; }
          .details { background: white; padding: 15px; border-left: 4px solid #667eea; margin: 15px 0; }
          .footer { text-align: center; color: #666; font-size: 12px; margin-top: 30px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Thank you for your inquiry!</h1>
            <p>We received your information and will contact you as soon as possible.</p>
          </div>

          <div class="content">
            <h2>Your information:</h2>
            <div class="details">
              <p><strong>Name:</strong> ${formData.name}</p>
              <p><strong>Company:</strong> ${formData.company || 'Not specified'}</p>
              <p><strong>Message:</strong> ${formData.message}</p>
            </div>

            ${formData.services.length > 0 ? `
              <div class="details">
                <p><strong>Interested Services:</strong></p>
                <p>${formData.services.join(', ')}</p>
              </div>
            ` : ''}

            ${formData.technologies.length > 0 ? `
              <div class="details">
                <p><strong>Technologies:</strong></p>
                <p>${formData.technologies.join(', ')}</p>
              </div>
            ` : ''}

            <p style="margin-top: 30px; color: #666;">
              Our team will review your request and contact you within 24 hours.
            </p>
          </div>

          <div class="footer">
            <p>© 2026 Bercastle. All rights reserved.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Sends team notification email via Resend
 */
export const sendTeamNotification = async (formData) => {
  try {
    const teamRecipients = [
      'alan@bercastle.com',
      'mark@bercastle.com'
    ];

    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from: 'noreply@bercastle.com',
        to: teamRecipients,
        cc: 'ndesign.by@gmail.com',
        subject: `🚀 New Project Request from ${formData.name}`,
        html: generateTeamNotificationTemplate(formData),
        replyTo: formData.email,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error sending team notification:', error);
    return false;
  }
};

/**
 * Generates HTML template for team notification email
 */
const generateTeamNotificationTemplate = (formData) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; }
          .container { max-width: 700px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 8px; }
          .section { padding: 20px; background: #f9fafb; border-radius: 8px; margin: 15px 0; }
          .field { margin: 12px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; }
          .field:last-child { border-bottom: none; }
          .label { color: #667eea; font-weight: bold; }
          .value { color: #555; margin-top: 4px; }
          .badge { display: inline-block; background: #667eea; color: white; padding: 4px 12px; border-radius: 4px; margin: 4px 4px 4px 0; font-size: 12px; }
          .footer { text-align: center; color: #666; font-size: 12px; margin-top: 30px; }
          .reply-note { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 12px; margin: 20px 0; font-size: 13px; color: #78350f; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🚀 New Project Request</h1>
            <p>A potential client has submitted a project inquiry.</p>
          </div>

          <div class="section">
            <h2>Client Information</h2>
            <div class="field">
              <div class="label">Name:</div>
              <div class="value">${formData.name}</div>
            </div>
            <div class="field">
              <div class="label">Email:</div>
              <div class="value">${formData.email}</div>
            </div>
            ${formData.company ? `
            <div class="field">
              <div class="label">Company:</div>
              <div class="value">${formData.company}</div>
            </div>
            ` : ''}
            <div class="field">
              <div class="label">Message:</div>
              <div class="value">${formData.message}</div>
            </div>
          </div>

          <div class="section">
            <h2>Project Details</h2>
            ${formData.services.length > 0 ? `
            <div class="field">
              <div class="label">Services Interested In:</div>
              <div class="value">
                ${formData.services.map(s => `<span class="badge">${s}</span>`).join('')}
              </div>
            </div>
            ` : ''}
            
            ${formData.technologies.length > 0 ? `
            <div class="field">
              <div class="label">Technologies:</div>
              <div class="value">
                ${formData.technologies.map(t => `<span class="badge">${t}</span>`).join('')}
              </div>
            </div>
            ` : ''}

            ${formData.budget ? `
            <div class="field">
              <div class="label">Budget:</div>
              <div class="value">${formData.budget}</div>
            </div>
            ` : ''}

            ${formData.timeline ? `
            <div class="field">
              <div class="label">Timeline:</div>
              <div class="value">${formData.timeline}</div>
            </div>
            ` : ''}

            ${formData.platforms.length > 0 ? `
            <div class="field">
              <div class="label">Platforms:</div>
              <div class="value">
                ${formData.platforms.map(p => `<span class="badge">${p}</span>`).join('')}
              </div>
            </div>
            ` : ''}

            ${formData.projectStatus.length > 0 ? `
            <div class="field">
              <div class="label">Project Status:</div>
              <div class="value">
                ${formData.projectStatus.map(s => `<span class="badge">${s}</span>`).join('')}
              </div>
            </div>
            ` : ''}

            ${formData.developmentStage ? `
            <div class="field">
              <div class="label">Development Stage:</div>
              <div class="value">${formData.developmentStage}</div>
            </div>
            ` : ''}

            ${formData.hasDesign ? `
            <div class="field">
              <div class="label">Design:</div>
              <div class="value">Has existing design</div>
            </div>
            ` : ''}

            ${formData.hasTechSpec ? `
            <div class="field">
              <div class="label">Technical Specification:</div>
              <div class="value">Has technical specification</div>
            </div>
            ` : ''}

            ${formData.prototypeLink ? `
            <div class="field">
              <div class="label">Prototype Link:</div>
              <div class="value"><a href="${formData.prototypeLink}">${formData.prototypeLink}</a></div>
            </div>
            ` : ''}
          </div>

          <div class="reply-note">
            💬 <strong>Reply Note:</strong> Click "Reply" to respond directly to ${formData.name} at ${formData.email}
          </div>

          <div class="footer">
            <p>Sent from Bercastle Project Management System</p>
            <p>© 2026 Bercastle. All rights reserved.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Sends survey/lead data to team via Resend
 */
export const sendSurveyLead = async (formData, leadId = null) => {
  try {
    const teamRecipients = [
      'alan@bercastle.com',
      'mark@bercastle.com'
    ];

    const d1Op = leadId
      ? updateLeadInD1(leadId, formData)
      : saveToD1(formData, 'survey');

    const promises = [
      fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: 'noreply@bercastle.com',
          to: teamRecipients,
          cc: 'ndesign.by@gmail.com',
          subject: `📊 Survey Lead from ${formData.name || 'Anonymous'}`,
          html: generateSurveyLeadTemplate(formData),
          replyTo: formData.email,
        }),
      }),
      d1Op,
    ];

    const [teamEmailResult] = await Promise.all(promises);

    return teamEmailResult.ok;
  } catch (error) {
    console.error('Error sending survey lead:', error);
    return false;
  }
};

/**
 * Generates HTML template for survey lead email
 */
const generateSurveyLeadTemplate = (formData) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; }
          .container { max-width: 700px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 8px; }
          .section { padding: 20px; background: #f9fafb; border-radius: 8px; margin: 15px 0; }
          .field { margin: 12px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; }
          .field:last-child { border-bottom: none; }
          .label { color: #667eea; font-weight: bold; }
          .value { color: #555; margin-top: 4px; }
          .badge { display: inline-block; background: #667eea; color: white; padding: 4px 12px; border-radius: 4px; margin: 4px 4px 4px 0; font-size: 12px; }
          .footer { text-align: center; color: #666; font-size: 12px; margin-top: 30px; }
          .reply-note { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 12px; margin: 20px 0; font-size: 13px; color: #78350f; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📊 New Survey Lead</h1>
            <p>Someone has completed the project survey.</p>
          </div>

          ${formData.email ? `
          <div class="section">
            <h2>Lead Contact</h2>
            <div class="field">
              <div class="label">Email:</div>
              <div class="value">${formData.email}</div>
            </div>
            ${formData.name ? `
            <div class="field">
              <div class="label">Name:</div>
              <div class="value">${formData.name}</div>
            </div>
            ` : ''}
            ${formData.company ? `
            <div class="field">
              <div class="label">Company:</div>
              <div class="value">${formData.company}</div>
            </div>
            ` : ''}
            ${formData.message ? `
            <div class="field">
              <div class="label">Message:</div>
              <div class="value">${formData.message}</div>
            </div>
            ` : ''}
          </div>
          ` : ''}

          <div class="section">
            <h2>Survey Responses</h2>
            
            ${formData.services.length > 0 ? `
            <div class="field">
              <div class="label">Services Interested In:</div>
              <div class="value">
                ${formData.services.map(s => `<span class="badge">${s}</span>`).join('')}
              </div>
            </div>
            ` : '<div class="field"><div class="value text-gray-400">No services selected</div></div>'}
            
            ${formData.technologies.length > 0 ? `
            <div class="field">
              <div class="label">Technologies:</div>
              <div class="value">
                ${formData.technologies.map(t => `<span class="badge">${t}</span>`).join('')}
              </div>
            </div>
            ` : ''}

            ${formData.budget ? `
            <div class="field">
              <div class="label">Budget:</div>
              <div class="value">${formData.budget}</div>
            </div>
            ` : ''}

            ${formData.timeline ? `
            <div class="field">
              <div class="label">Timeline:</div>
              <div class="value">${formData.timeline}</div>
            </div>
            ` : ''}

            ${formData.platforms.length > 0 ? `
            <div class="field">
              <div class="label">Target Platforms:</div>
              <div class="value">
                ${formData.platforms.map(p => `<span class="badge">${p}</span>`).join('')}
              </div>
            </div>
            ` : ''}
          </div>

          ${formData.email ? `
          <div class="reply-note">
            💬 <strong>Reply Note:</strong> Click "Reply" to respond directly to ${formData.name || 'the lead'} at ${formData.email}
          </div>
          ` : ''}

          <div class="footer">
            <p>Sent from Bercastle.com</p>
            <p>© 2026 Bercastle. All rights reserved.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Main function for processing full submission
 */
export const submitProjectRequest = async (formData) => {
  try {
    // Save to D1 (Slack notification happens server-side in save-lead)
    const d1Result = await saveToD1(formData, 'contact');

    return {
      success: !!d1Result,
      d1: d1Result,
    };
  } catch (error) {
    console.error('Error submitting request:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};
